import {
  EinvoicingAdminDataQuery,
  PeppolRegistrationMatchResult,
} from '../../generated/graphql';

export type AdminVenue =
  EinvoicingAdminDataQuery['einvoicingAdminVenues'][number];
export type AdminInbox =
  EinvoicingAdminDataQuery['einvoicingAdminInboxes'][number];

export interface PendingRequest {
  id: string;
  legalName: string;
  ico: string;
  dic: string;
  peppolId: string;
  kind: 'match' | 'partial' | 'none';
  receivedAt: string;
  notes: string[];
  suggestedVenueId?: string;
}

export interface InboxOption {
  peppolId: string;
  legalName: string;
  ico: string;
  dic: string;
  assignedVenues: string[];
  pendingNote: string | null;
}

const digitsOnly = (value: string | null | undefined): string =>
  (value ?? '').replace(/\D/g, '');

const dicDigits = (value: string | null | undefined): string => {
  const raw = (value ?? '').trim().toUpperCase();
  const withoutPrefix = raw.startsWith('SK') ? raw.slice(2) : raw;
  return digitsOnly(withoutPrefix);
};

const dicMatches = (
  venue: AdminVenue,
  inbox: Pick<AdminInbox, 'dic'>,
): boolean => {
  const left = dicDigits(venue.dic);
  const right = dicDigits(inbox.dic);
  return Boolean(left && right && left === right);
};

export const buildPendingRequests = (
  venues: AdminVenue[],
  queue: EinvoicingAdminDataQuery['peppolRegistrationQueue'],
): PendingRequest[] =>
  queue.map((item) => {
    const peppolId = item.peppolParticipantId ?? '';
    const inbox = {
      ico: item.ico,
      dic: item.dic,
    };
    const notes: string[] = [];
    let suggestedVenueId: string | undefined;

    const dicVenues = venues.filter((venue) => dicMatches(venue, inbox));
    const partialVenues = dicVenues.filter(
      (venue) =>
        venue.ico &&
        inbox.ico &&
        digitsOnly(venue.ico) !== digitsOnly(inbox.ico),
    );

    if (partialVenues.length > 0) {
      const venue = partialVenues[0];
      suggestedVenueId = venue.connected ? undefined : venue.id;
      notes.push(`DIČ matches ${venue.name}.`);
      if (venue.ico && inbox.ico) {
        notes.push(
          `IČO does not. The venue IČO is ${venue.ico} and the registration IČO is ${inbox.ico}.`,
        );
      }
    } else if (dicVenues.length === 0) {
      notes.push('No Slovak venue has this IČO or DIČ.');
    } else if (dicVenues.length === 1 && !dicVenues[0].connected) {
      suggestedVenueId = dicVenues[0].id;
    }

    let kind: PendingRequest['kind'] = 'none';
    if (item.matchResult === PeppolRegistrationMatchResult.Match) {
      kind = 'match';
    } else if (item.matchResult === PeppolRegistrationMatchResult.Partial) {
      kind = 'partial';
    }

    return {
      id: item.id,
      legalName: item.legalName ?? '—',
      ico: item.ico ?? '—',
      dic: item.dic ?? '—',
      peppolId,
      kind,
      receivedAt: item.receivedAt,
      notes,
      suggestedVenueId,
    };
  });

export const buildInboxOptions = (
  venues: AdminVenue[],
  inboxes: AdminInbox[],
  requests: PendingRequest[],
): InboxOption[] => {
  const byId = new Map<string, InboxOption>();
  const add = (
    peppolId: string,
    legalName: string,
    ico: string,
    dic: string,
  ) => {
    if (!peppolId || byId.has(peppolId)) return;
    byId.set(peppolId, {
      peppolId,
      legalName,
      ico,
      dic,
      assignedVenues: [],
      pendingNote: null,
    });
  };

  inboxes.forEach((inbox) => {
    if (!inbox.peppolParticipantId) return;
    add(
      inbox.peppolParticipantId,
      inbox.legalName ?? '—',
      inbox.ico ?? '—',
      inbox.dic ?? '—',
    );
    const row = byId.get(inbox.peppolParticipantId);
    if (row && inbox.venueNames.length > 0) {
      row.assignedVenues = [...inbox.venueNames];
    }
  });

  requests.forEach((request) => {
    add(request.peppolId, request.legalName, request.ico, request.dic);
    const row = byId.get(request.peppolId);
    if (row && row.legalName !== request.legalName) {
      row.pendingNote = `${request.legalName} · IČO ${request.ico} · DIČ ${request.dic}`;
    }
  });

  venues.forEach((venue) => {
    if (!venue.peppolParticipantId) return;
    add(
      venue.peppolParticipantId,
      venue.name,
      venue.ico ?? '—',
      venue.dic ?? '—',
    );
    const row = byId.get(venue.peppolParticipantId);
    if (row) {
      row.assignedVenues = [...row.assignedVenues, venue.name];
    }
  });

  return [...byId.values()];
};

export const idsDiffer = (venue: AdminVenue, request: PendingRequest) =>
  digitsOnly(venue.ico) !== digitsOnly(request.ico) &&
  dicDigits(venue.dic) !== dicDigits(request.dic);
